/**
 * One UK date format for staff screens: "14 Oct 2026", "14 Oct 2026, 06:41", always in UK time (clock change included).
 *
 * The Fora Sync page used to show dates three ways (browser-locale month-first, "14/10/26", and ISO), so the same day
 * read differently from one card to the next. Everything on screens that show a date or a time goes through these.
 * A date-only value ("2026-10-14") is shown as that calendar day, never shifted by a time zone.
 */
const UK_ZONE = 'Europe/London';

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

const dateFormat = new Intl.DateTimeFormat('en-GB', {
  timeZone: UK_ZONE,
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const timeFormat = new Intl.DateTimeFormat('en-GB', {
  timeZone: UK_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const dayKeyFormat = new Intl.DateTimeFormat('en-CA', {
  timeZone: UK_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** "14 Oct 2026". A plain date ("2026-10-14") keeps its calendar day. Empty or unreadable input gives the fallback. */
export function formatUkDate(value: string | number | Date | null | undefined, fallback = '—'): string {
  if (typeof value === 'string') {
    const match = DATE_ONLY.exec(value.trim());
    if (match) {
      // Noon UTC is the same calendar day in London all year round.
      const noon = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12));
      return Number.isNaN(noon.getTime()) ? fallback : dateFormat.format(noon);
    }
  }
  const date = toDate(value);
  return date ? dateFormat.format(date) : fallback;
}

/** "06:41" in UK time. */
export function formatUkTime(value: string | number | Date | null | undefined, fallback = '—'): string {
  const date = toDate(value);
  return date ? timeFormat.format(date) : fallback;
}

/** "14 Oct 2026, 06:41" in UK time. */
export function formatUkDateTime(value: string | number | Date | null | undefined, fallback = '—'): string {
  const date = toDate(value);
  return date ? `${dateFormat.format(date)}, ${timeFormat.format(date)}` : fallback;
}

/** Today's calendar day in the UK as "2026-10-14" (what a date column compares against). */
export function ukDayKey(now: Date = new Date()): string {
  return dayKeyFormat.format(now);
}
